import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { LoggingMessageService } from 'src/app/services/logging-message.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { UserNavigationService } from 'src/app/services/user-navigation.service';
import { DomSanitizer } from '@angular/platform-browser';
import { WindowEventsService } from 'src/app/services/window-events.service';
import { BaseWebsiteComponent } from '../base-website/base-website.component';
import { IframeService } from 'src/app/services/iframe.service';
import { PendoService } from 'src/app/services/pendo.service';
import { SubscriptionChangeService } from 'src/app/services/subscription-change.service';

@Component({
    standalone: true,
    selector: 'app-portal',
    templateUrl: './portal.component.html',
    styleUrls: ['./portal.component.scss']
})

export class PortalComponent
    extends BaseWebsiteComponent
    implements OnInit, OnDestroy {
    private PORTAL_REPORTS = 'qidashboard.aspx';
    public portalClass = '';

    constructor(
        public router: Router,
        public route: ActivatedRoute,
        public logger: LoggingMessageService,
        public sessionService: SessionStorageService,
        public navigationService: UserNavigationService,
        public authService: AuthService,
        public windowEvents: WindowEventsService,
        public iframeService: IframeService,
        public pendoService: PendoService,
        public sanitizer: DomSanitizer,
        public subscriptionService: SubscriptionChangeService) {

        super(router, route, logger, sessionService, navigationService, authService, windowEvents, iframeService, pendoService, sanitizer, subscriptionService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.selectedSubscriptionID = this.subscriptionService.SelectedSubscription;
        this.availableSubscriptions = this.subscriptionService.AvailableSubscriptionsString;
    }

    subscribeToChildSiteEvents() {
        this.subscribePortalLogin();
        this.subscribePortalLogout();

        const pLoadedSubscription = this.iframeService.portalFrameLoaded$
            .subscribe(() => this.setIframeHeight());
        this.$subscriptions.add(pLoadedSubscription);

        this.subscribePortalActionEvents();
        this.subscribeSubDomainActionEvents();
    }

    getIframeReferences() {
        if (!this.visibleIframe) {
            this.visibleIframe = document.getElementById('appPortalIframe') as HTMLIFrameElement;
        }
    }

  loadIframe(path: string) {
        const loggedIn = this.authService.isUserLoggedIn();
        if ((loggedIn === true) && (path !== '/login')) {
            if (this.isDownloadUrl(path)) {
                this.downloadFile(path);
            } else if (this.isPdfUrl(path)) {
                this.openInNewTab(path);
            } else {
                const displayedMenu = this.navigationService.DisplayedNavigationMenu;
                if (displayedMenu && displayedMenu.Items && displayedMenu.Items.length > 0) {
                    this.setIframeSource(path);
                    this.checkAccessToken(path);
                    this.iframeService.setPortalFrameVisibility(true);
                    this.iframeService.setLinkId('');
                }
            }
        } else {
            this.setIframeSourceWithLoginLogout(path);
        }
        this.setIframeClass(path);
    }

  setIframeSourceWithLoginLogout(path: string) {
        if (path.includes(environment.LogoutPage)) {
            this.visibleSrc = this.sanitizer.bypassSecurityTrustResourceUrl(path);
            this.iframeService.setPortalFrameSource(path);
            this.iframeService.setPortalFrameVisibility(true);
        } else if (environment.production === true) {
            const loginUrl = environment.LoginPage;
            this.visibleSrc = this.sanitizer.bypassSecurityTrustResourceUrl(loginUrl);
            this.iframeService.setPortalFrameSource(loginUrl);
            this.iframeService.setPortalFrameVisibility(true);
        } else {
            this.router.navigate(['/sso']);
        }
    }

    private setIframeClass(path: string) {
        let portalClass = '';
        if (path) {
            const isReportPath = path.toLowerCase().includes(this.PORTAL_REPORTS);
            if (isReportPath) {
                portalClass = 'legacy reports';
            }
        }
        this.portalClass = portalClass;
    }

    setIframeSource(path: string) {
        const iframe = this.visibleIframe;
        if (iframe) {
            path = path.replace('/if', '');
            if (path.startsWith('/')) {
                path = `${environment.WebServer}${path}`;
            } else {
                path = `${environment.WebServer}/${path}`;
            }

            this.logger.debug('portal', 'setIframeSource', this.selectedSubscriptionID, this.availableSubscriptions, `Src: ${path}`, '');
            this.previousUrl = iframe.src;
            this.currentUrl = path;
            if (((this.currentUrl) && (this.currentUrl.includes('tutorials/ModuleHomePage.aspx')))) {
                if (this.previousUrl) {
                    this.backButtonUrl = this.previousUrl;
                } else {
                    const routeObject = this.router.getCurrentNavigation();
                    this.backButtonUrl = routeObject.previousNavigation.extractedUrl.toLocaleString();
                }
            }
            this.showBackButton = false;
            if (environment.production === true) {
                this.visibleSrc = this.sanitizer.bypassSecurityTrustResourceUrl(path);
                this.logger.debug('portal', 'setIframeSource', this.selectedSubscriptionID, this.availableSubscriptions, `Setting Portal Source - Src: ${path}`, '');
                this.iframeService.setPortalFrameSource(path);
                this.navigationService.emitNavigationComplete(this.linkid);
            } else {
                this.iframeService.setPortalFrameSource(environment.PortalWebServer);
            }
        }
    }

    setIframeHeight() {
        const iframe = this.visibleIframe;
        if (iframe) {
            iframe.style.height = '1000px';
        }
        if (!this.timer) {
            this.timer = setInterval(() => {
                this.updateIframeHeight();
            }, 5000);
        }
    }

    public updateIframeHeight() {
        const iframe = this.visibleIframe;
        if (iframe && iframe.contentWindow) {
            const isLocalhost = iframe.src.includes('localhost');
            if (!isLocalhost && iframe.contentWindow.document && iframe.contentWindow.document.body) {
                const height = iframe.contentWindow.document.body.scrollHeight;
                if (height > 1000) {
                    iframe.style.height = (height + 50) + 'px';
                }
            }
        }
    }

    private isPdfUrl(path: string): boolean {
        if (!path) {
            return false;
        }
        // return path.includes('.xml') || path.includes('.xsd') || path.includes('.mdb') || path.includes('.xls');
        return path.includes('.pdf');
    }

    private isDownloadUrl(path: string): boolean {
        if (!path) {
            return false;
        }
        // return path.includes('.xml') || path.includes('.xsd') || path.includes('.mdb') || path.includes('.xls');
        // return path.includes('.pdf') || path.includes('.xml') || path.includes('.xsd') || path.includes('.mdb');
        return path.includes('.xml') || path.includes('.xsd') || path.includes('.mdb');
    }

    private isExtendedDownloadUrl(path: string): boolean {
        if (!path) {
            return false;
        }
        return path.includes('.xls');
    }

    private openInNewTab(path: string) {
        const isLoggedIn = this.authService.isUserLoggedIn();
        if (isLoggedIn) {
            const fileUrl = `${environment.WebServer}/${path.replace('/if/', '')}`;
            window.open(fileUrl, '_blank');
        }
    }

    private downloadFile(path: string) {
        const isLoggedIn = this.authService.isUserLoggedIn();
        if (isLoggedIn) {
            // Create an invisible A element
            const a = document.createElement('a');
            const fileUrl = `${environment.WebServer}/${path.replace('/if/', '')}`;
            let fileName = path.replace(/^.*[\\\/]/, '');
            fileName = fileName.substring(0, fileName.indexOf('?'));
            a.target = '_blank';
            a.style.display = 'none';
            a.href = fileUrl;
            a.download = fileName;
            document.body.appendChild(a);
            // Trigger the download by simulating click
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(a.href);
            document.body.removeChild(a);
        }
    }

    private isPortalUrl(link) {
        if (link.includes('blankpage') || link.includes('log') || link.includes('empty-page') || link.toLocaleLowerCase().includes('portal')) {
            return true;
        }
        return false;
    }

    goBack() {
        const webserver = environment.WebServer;
        const url = this.backButtonUrl;
        const path = url.replace(webserver, '');
        this.updateLinkId();
        if (path.includes('blankpage') || path.includes('empty-page')) {
            this.loadIframe(environment.EmptyPage);
        } else {
            this.loadIframe(path);
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.iframeService.setPortalFrameVisibility(false);
        // this.iframeService.setPortalFrameSource('/');
    }
}
